import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Wrapper } from './style';
import { Link } from 'react-router-dom';
import { DatePicker, message, Select, Button, Spin } from 'antd';
import { FilterOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

// Config & API imports
import { GetMarketplaceDropdown, GetMarketplaceList, rangePresets, getDefaultSelectedMarketplaceId, findMarketplaceById } from '../../../config';
import { MakeApiCall } from '../../../apis';
import {
    buildDemoDashboardRangePresets,
    disabledDateForDemoDashboard,
    getDefaultOtherRangeForDemoDashboard,
    isDemoDashboardDateUser,
    validateDemoDashboardDayjs,
    validateDemoDashboardRange,
} from '../../../utils/dashboardDemoDateRange';

// Child Components
import TilesV2 from './tiles';
import ChartComponentV2 from './chart';
import PLV2 from './pl';
import { Helmet } from 'react-helmet';
import SyncToast from '../../../components/toast/SyncToast';
import SetupStepsBanner from "../../../components/onboarding/Setupstepsbanner";
import FeatureLockedOverlay from "../../../components/modal/Featurelockedoverlay"; // shared floating toast

const { Option } = Select;
const { RangePicker } = DatePicker;

const DEFAULT_PRODUCT_IMAGE = 'https://app.sellerview.ai/sellerview_api/images/no-product-image.png';
const AMAZON_DATE_FORMAT = 'YYYY-MM-DD';

const DashboardV2 = ({ userData }) => {
    // --- Connection Logic ---
    const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
    const isDataSyncing = isSpApiConnected && userData?.user?.user_data?.is_data_sync_email_sent === null;
    const trialExpired = !!(userData?.user?.user_data?.trial_expires_at && Date.now() > parseInt(userData.user.user_data.trial_expires_at, 10) * 1000);

    const currencySymbol = userData?.user?.user_data?.connected_marketplace?.currency_symbol;
    const selectedCurrencySign = currencySymbol || '₹';

    // --- State ---
    const [activeTab, setActiveTab] = useState('charts');
    const [isSearching, setIsSearching] = useState(false);
    const [marketplaceList, setMarketplaceList] = useState([]);
    const [selectedMarketplace, setSelectedMarketplace] = useState(null);
    const [filterData, setFilterData] = useState({});
    const [selectedValue, setSelectedValue] = useState(null);
    const [customRange, setCustomRange] = useState([]);
    const [asinList, setAsinList] = useState([]);
    const [selectedAsins, setSelectedAsins] = useState([]);
    const [periodicity, setPeriodicity] = useState('day');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // --- Pagination & Search States ---
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchText, setSearchText] = useState('');

    // Mobile filter toggle
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Sync toast dismissed state — resets if sync restarts
    const [syncToastDismissed, setSyncToastDismissed] = useState(false);

    const [otherRange, setOtherRange] = useState({
        start_date: dayjs().subtract(29, 'day').format(AMAZON_DATE_FORMAT),
        end_date: dayjs().format(AMAZON_DATE_FORMAT),
    });

    // Reset dismissed state if a new sync cycle begins
    useEffect(() => {
        if (isDataSyncing) setSyncToastDismissed(false);
    }, [isDataSyncing]);

    // --- Helpers & Memoized Values ---
    const dateFilterOptions = useMemo(() =>
            Object.entries(filterData).map(([key, group]) => ({
                label: Object.values(group).join(' / '),
                value: key,
            })),
        [filterData]
    );

    // FIX: Extract SKU directly from the composite "asin:::sku" value
    const selectedSkus = useMemo(() =>
            selectedAsins.map(val => val.split(':::')[1]).filter(Boolean),
        [selectedAsins]
    );

    const isDemoDateUser = useMemo(() => isDemoDashboardDateUser(userData), [userData]);

    const dashboardRangePresets = useMemo(
        () => (isDemoDateUser ? buildDemoDashboardRangePresets() : rangePresets),
        [isDemoDateUser]
    );

    // --- API Logic ---
    const fetchProductList = async (search = '', pageNum = 1, append = false) => {
        if (!isSpApiConnected) return;
        setIsSearching(true);
        try {
            const url = `get-products?page=${pageNum}&per-page=10${search ? `&search_text=${search}` : ''}`;
            const response = await MakeApiCall(url, 'get', null, true, {}, userData?.token);

            if (response?.status) {
                const records = response?.data?.records || [];
                const formattedData = records.map(item => ({
                    asin: item.asin,
                    name: item.item_name,
                    image: item.item_image,
                    sku: item.sku,
                }));

                setAsinList(prev => (append ? [...prev, ...formattedData] : formattedData));
                setHasMore(records.length === 10);
            }
        } catch (error) {
            console.error('Product fetch error:', error);
        } finally {
            setIsSearching(false);
        }
    };

    const debouncedSearch = useCallback(
        (() => {
            let timeout;
            return (val) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => {
                    setPage(1);
                    setSearchText(val);
                    fetchProductList(val, 1, false);
                }, 500);
            };
        })(),
        [userData?.token, isSpApiConnected]
    );

    const handleSearchBlur = () => {
        setSearchText('');
        setPage(1);
        fetchProductList('', 1, false);
    };

    const handlePopupScroll = (e) => {
        const { target } = e;
        if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
            if (!isSearching && hasMore) {
                const nextBatch = page + 1;
                setPage(nextBatch);
                fetchProductList(searchText, nextBatch, true);
            }
        }
    };

    const loadInitialData = async () => {
        if (!isSpApiConnected) return;
        try {
            const [mktResponse, filterResponse] = await Promise.all([
                GetMarketplaceDropdown(),
                MakeApiCall(`dashboard-optimized/tiles/get-filters-data`, 'get', null, true, {}, userData?.token),
            ]);

            if (mktResponse?.marketplace_list?.length > 0) {
                setMarketplaceList(mktResponse.marketplace_list);
                const defaultMktId = getDefaultSelectedMarketplaceId(
                    mktResponse.selected_marketplace,
                    userData?.user?.user_data?.connected_marketplace?.marketplace_id
                );
                setSelectedMarketplace(findMarketplaceById(mktResponse.marketplace_list, defaultMktId));
            }

            if (filterResponse?.status) setFilterData(filterResponse.data || {});

            await fetchProductList('', 1, false);
        } catch (e) {
            message.error('Failed to sync dashboard data.');
        }
    };

    // --- Effects ---
    useEffect(() => {
        loadInitialData();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isSpApiConnected]);

    useEffect(() => {
        if (Object.keys(filterData).length > 0 && !selectedValue) {
            setSelectedValue(Object.keys(filterData)[0]);
        }
    }, [filterData]);

    useEffect(() => {
        if (!isDemoDateUser) return;
        setOtherRange((prev) => {
            const s = dayjs(prev.start_date);
            const e = dayjs(prev.end_date);
            const v = validateDemoDashboardRange(s, e);
            if (v.ok) return prev;
            return getDefaultOtherRangeForDemoDashboard();
        });
    }, [isDemoDateUser]);

    // --- Event Handlers ---
    const handleMobileDateChange = (date, type) => {
        if (!date) return;
        if (isDemoDateUser) {
            const single = validateDemoDashboardDayjs(date);
            if (!single.ok) {
                message.error(single.message);
                return;
            }
        }
        setOtherRange((prev) => {
            const next = {
                ...prev,
                [type === 'start' ? 'start_date' : 'end_date']: date.format(AMAZON_DATE_FORMAT),
            };
            if (isDemoDateUser) {
                const rangeOk = validateDemoDashboardRange(dayjs(next.start_date), dayjs(next.end_date));
                if (!rangeOk.ok) {
                    message.error(rangeOk.message);
                    return prev;
                }
            }
            return next;
        });
    };

    const handleRangeOther = (dates) => {
        if (!isDemoDateUser) {
            const [start, end] = dates || [null, null];
            setOtherRange({
                start_date: (start || dayjs().subtract(29, 'day')).format(AMAZON_DATE_FORMAT),
                end_date: (end || dayjs()).format(AMAZON_DATE_FORMAT),
            });
            return;
        }
        if (!dates || !dates[0] || !dates[1]) {
            return;
        }
        const [start, end] = dates;
        const v = validateDemoDashboardRange(start, end);
        if (!v.ok) {
            message.error(v.message);
            return;
        }
        setOtherRange({
            start_date: start.format(AMAZON_DATE_FORMAT),
            end_date: end.format(AMAZON_DATE_FORMAT),
        });
    };

    const renderTab = (key, label, iconClass) => (
        <li className="nav-item" key={key}>
            <Link
                className={`nav-link ${activeTab === key ? 'active' : ''}`}
                onClick={(e) => {
                    if (!isSpApiConnected) e.preventDefault();
                    setActiveTab(key);
                }}
            >
                <i className={`ki-outline ${iconClass} me-3 fs-2 text-gray-900`}></i>
                {label}
            </Link>
        </li>
    );

    return (
        <Wrapper>
            <Helmet><title>P&L Analytics V2</title></Helmet>
            <SetupStepsBanner userData={userData} />

            {/* Floating Sync Toast — uses shared SyncToast component */}
            <SyncToast
                visible={isDataSyncing && !syncToastDismissed}
                onDismiss={() => setSyncToastDismissed(true)}
            />

            <div style={{ position: 'relative' }}>
                {/* Locked Overlay */}
                <FeatureLockedOverlay locked={trialExpired || !isSpApiConnected} title="Features Locked" message={trialExpired ? "Your trial has expired. Please renew your subscription to continue." : "Connect your Amazon account to unlock analytics."}/>

                {/* Tabs Section */}
                <div className="mb-5 mx-md-n9 mx-n2 px-10 py-5 bg-white filter">
                    <div className="position-relative d-flex wrap justify-content-between align-items-center">
                        <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 d-inline-flex">
                            {renderTab('charts', 'Overview', 'ki-chart-pie-4')}
                            {renderTab('pl', 'P&L Trends', 'ki-chart-line-up')}
                        </ul>
                    </div>
                </div>

                {/* Mobile Filter Button */}
                {isMobile && isSpApiConnected && (
                    <div className="px-1 mb-4">
                        <Button
                            icon={showMobileFilters ? <CloseOutlined /> : <FilterOutlined />}
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                            className="w-100 btn btn-danger fw-bold d-md-none d-flex align-items-center justify-content-center"
                            type={showMobileFilters ? 'default' : 'primary'}
                            size="large"
                            style={{ height: '48px', borderRadius: '8px' }}
                        >
                            {showMobileFilters ? 'Filters' : 'Filters'}
                        </Button>
                    </div>
                )}

                {/* Filters Section */}
                {(!isMobile || showMobileFilters) && (
                    <div
                        className={`mb-5 d-flex flex-column flex-md-row gap-4 filters w-100 overflow-hidden px-1 ${
                            isMobile ? 'animate__animated animate__fadeIn' : ''
                        }`}
                    >
                        {/* ASIN / SKU Search Select */}
                        <Select
                            mode="multiple"
                            showSearch
                            placeholder="Search SKU, Title or ASIN"
                            className="w-100 flex-md-grow-0"
                            style={{ borderColor: 'black', width: isMobile ? '100%' : '350px' }}
                            size="large"
                            value={selectedAsins}
                            onChange={setSelectedAsins}
                            onSearch={debouncedSearch}
                            onBlur={handleSearchBlur}
                            onPopupScroll={handlePopupScroll}
                            filterOption={false}
                            loading={isSearching && isSpApiConnected}
                            optionLabelProp="label"
                            maxTagCount="responsive"
                            disabled={!isSpApiConnected}
                            notFoundContent={
                                isSearching
                                    ? <div className="text-center p-2"><Spin size="small" /></div>
                                    : null
                            }
                        >
                            {asinList.map(item => (
                                <Option
                                    key={`${item.asin}:::${item.sku || ''}`}
                                    value={`${item.asin}:::${item.sku || ''}`}
                                    label={item.sku || item.asin}
                                >
                                    <div className="d-flex align-items-center gap-3 py-1">
                                        <img
                                            src={item.image || DEFAULT_PRODUCT_IMAGE}
                                            alt=""
                                            className="w-30px h-30px rounded border object-fit-contain"
                                        />
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <div
                                                className="fw-bolder text-dark text-truncate"
                                                style={{ fontSize: '12px' }}
                                                title={item.name}
                                            >
                                                {item.name}
                                            </div>
                                            <div className="fs-12 text-muted d-flex align-items-center" style={{ gap: '8px' }}>
                        <span
                            className="badge badge-light-danger fw-bold"
                            title={`ASIN: ${item.asin}`}
                        >
                          {item.asin}
                        </span>
                                                {item.sku && (
                                                    <span
                                                        className="text-truncate"
                                                        style={{ maxWidth: '160px' }}
                                                        title={`SKU: ${item.sku}`}
                                                    >
                            <small className="text-muted">
                              SKU: <span className="fw-bold text-dark">{item.sku}</span>
                            </small>
                          </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Option>
                            ))}

                            {isSearching && hasMore && (
                                <Option disabled key="loading-more" className="text-center bg-light">
                                    <Spin size="small" /> <small className="ms-2">Loading more...</small>
                                </Option>
                            )}
                        </Select>

                        {/* Date Range / Periodicity */}
                        <div className="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
                            {activeTab === 'tiles' ? (
                                <Select
                                    className="w-100 w-md-250px"
                                    placeholder="Select Date Range"
                                    onChange={(val) => {
                                        setSelectedValue(val);
                                        if (val !== 'custom') setCustomRange([]);
                                    }}
                                    value={selectedValue}
                                    size="large"
                                    options={dateFilterOptions}
                                    disabled={!isSpApiConnected}
                                />
                            ) : isMobile ? (
                                <div className="d-flex gap-2 w-100">
                                    <DatePicker
                                        disabled={!isSpApiConnected}
                                        value={dayjs(otherRange.start_date)}
                                        placeholder="Start"
                                        size="large"
                                        className="w-50"
                                        disabledDate={isDemoDateUser ? disabledDateForDemoDashboard : undefined}
                                        onChange={(d) => handleMobileDateChange(d, 'start')}
                                    />
                                    <DatePicker
                                        disabled={!isSpApiConnected}
                                        value={dayjs(otherRange.end_date)}
                                        placeholder="End"
                                        size="large"
                                        className="w-50"
                                        disabledDate={isDemoDateUser ? disabledDateForDemoDashboard : undefined}
                                        onChange={(d) => handleMobileDateChange(d, 'end')}
                                    />
                                </div>
                            ) : (
                                <RangePicker
                                    disabled={!isSpApiConnected}
                                    size="large"
                                    presets={dashboardRangePresets}
                                    disabledDate={isDemoDateUser ? disabledDateForDemoDashboard : undefined}
                                    value={[dayjs(otherRange.start_date), dayjs(otherRange.end_date)]}
                                    onChange={handleRangeOther}
                                    className="w-md-250px"
                                />
                            )}

                            {activeTab !== 'tiles' && (
                                <Select
                                    value={periodicity}
                                    style={{ width: isMobile ? '100%' : '120px' }}
                                    onChange={setPeriodicity}
                                    size="large"
                                    disabled={!isSpApiConnected}
                                >
                                    <Option value="day">By Day</Option>
                                    <Option value="month">By Month</Option>
                                </Select>
                            )}
                        </div>

                        {/* Marketplace Select */}
                        <Select
                            className="w-100 w-md-200px"
                            size="large"
                            placeholder="Select Marketplace"
                            options={GetMarketplaceList(marketplaceList)}
                            value={selectedMarketplace?.id}
                            onChange={(id) => setSelectedMarketplace(marketplaceList.find(m => m.id === id))}
                            disabled={!isSpApiConnected}
                        />
                    </div>
                )}

                {/* Main Content Area */}
                <div className="w-100" style={{ overflowX: 'clip' }}>
                    {(() => {
                        const locked = trialExpired || !isSpApiConnected;
                        if (locked) {
                            return (
                                <div className="card card-xxl-stretch mb-5 mb-xl-8">
                                    <div className="card-body text-center py-10">
                                        <h5 className="mb-2">Features are locked</h5>
                                        <p className="text-muted mb-0">{trialExpired ? 'Your trial has expired. Please renew to access analytics.' : 'Connect your Amazon account to unlock analytics.'}</p>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <>
                                {activeTab === 'tiles' && (
                                    <TilesV2
                                        customRange={customRange}
                                        selectedValue={selectedValue}
                                        userData={userData}
                                        selectedMarketplace={selectedMarketplace}
                                        selectedCurrencySign={selectedCurrencySign}
                                    />
                                )}
                                {activeTab === 'charts' && (
                                    <ChartComponentV2
                                        periodicity={periodicity}
                                        otherRange={otherRange}
                                        userData={userData}
                                        selectedMarketplace={selectedMarketplace}
                                        selectedCurrencySign={selectedCurrencySign}
                                        selectedSku={selectedSkus}
                                    />
                                )}
                                {activeTab === 'pl' && (
                                    <PLV2
                                        periodicity={periodicity}
                                        otherRange={otherRange}
                                        userData={userData}
                                        selectedMarketplace={selectedMarketplace}
                                        selectedCurrencySign={selectedCurrencySign}
                                        selectedSku={selectedSkus}
                                    />
                                )}
                            </>
                        );
                    })()}
                </div>
            </div>
        </Wrapper>
    );
};

export default DashboardV2;

